import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/th";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {
  confirmBdcDo,
  exportBdcControlSheet,
  exportBdcShipments,
  issueBdcInvoice,
  listBdcShipments,
} from "../../../services";
import { featureAccess } from "../../../permissions/access";
import { useUserRole } from "../../../utils/hooks/useUserRole";
import { BdcBranch, BdcShipmentFilter, BdcShipmentRow, BdcShipmentStatus } from "../../../types/bdc.type";
import { issueFailureKind, readBdcError } from "../apiError";
import BranchChip from "../Branch/BranchChip";
import BranchSelector from "../Branch/BranchSelector";
import { ALL_BRANCHES } from "../Branch/branches";
import TestModeBanner from "../Branch/TestModeBanner";
import { useBdcBranchSelection, useBdcBranches } from "../Branch/useBdcBranches";
import { BDC_QUEUE_LIMIT, bdcDocumentsLink, bdcEdiLink, bdcShipmentLink } from "../constants";
import { baht, pcs, roundLabel, thaiDate, todayBkk } from "../format";
import KpiRow from "../KpiRow";
import { BDC_STATUS_META, BDC_STATUS_ORDER, countByStatus, isBdcShipmentStatus } from "../status";
import StatusChip from "../StatusChip";
import { CARD_SX, SCROLL_SX, TABLE_SX, TAG_COLORS } from "../styles";
import Tag from "../Tag";
import { useBdcDcs } from "../useBdcDcs";
import { useBlobExport } from "../useBlobExport";
import { BULK_LABEL, BulkAction, BulkResult, bulkTargets, runBulk } from "./bulk";

type DateMode = "order" | "delivery";
const ALL = "all";
const ROUND_OPTIONS = [1, 2, 3];

const sum = (rows: BdcShipmentRow[], pick: (row: BdcShipmentRow) => number) =>
  rows.reduce((total, row) => total + (pick(row) ?? 0), 0);

const delivered = (row: BdcShipmentRow) => row.delivery_pieces ?? row.pieces;

function ShipmentTable({
  title,
  rows,
  branches,
  selected,
  selectable,
  onToggle,
  onToggleAll,
  onOpen,
}: Readonly<{
  title: string;
  rows: BdcShipmentRow[];
  branches: BdcBranch[];
  selected: ReadonlySet<string>;
  selectable: boolean;
  onToggle: (uuid: string) => void;
  onToggleAll: (rows: BdcShipmentRow[], on: boolean) => void;
  onOpen: (row: BdcShipmentRow) => void;
}>) {
  const ticked = rows.filter((r) => selected.has(r.shipment_uuid)).length;
  return (
    <Card sx={CARD_SX}>
      <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #E3E5EA", display: "flex", gap: 1.5, alignItems: "center" }}>
        <Typography fontWeight={600}>{title}</Typography>
        <Tag tone="mut">{rows.length} shipment</Tag>
        <Typography className="sub">
          {pcs(sum(rows, (r) => r.pieces))} ชิ้น · {pcs(sum(rows, (r) => r.full_cases))} ลังเต็ม
        </Typography>
      </Box>
      {rows.length === 0 ? (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography className="sub">ไม่มีรายการ</Typography>
        </Box>
      ) : (
        <Box sx={SCROLL_SX}>
          <table className="bdc-t">
            <thead>
              <tr>
                {selectable && (
                  <th>
                    <Checkbox
                      size="small"
                      checked={ticked === rows.length}
                      indeterminate={ticked > 0 && ticked < rows.length}
                      onChange={(e) => onToggleAll(rows, e.target.checked)}
                      slotProps={{ input: { "aria-label": `เลือกทั้งหมด ${title}` } }}
                    />
                  </th>
                )}
                <th>สาขา</th>
                <th>DC</th>
                <th>รอบ</th>
                <th>เลข PO</th>
                <th>วันที่สั่ง</th>
                <th>วันส่ง</th>
                <th className="n">ร้าน</th>
                <th className="n">สาย</th>
                <th className="n">SKU</th>
                <th className="n">ชิ้น</th>
                <th className="n">ลังเต็ม</th>
                <th className="n">ก่อน VAT</th>
                <th className="n">VAT</th>
                <th className="n">ยอดรวม VAT</th>
                <th>สถานะ</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.shipment_uuid || row.po_number} className="click" onClick={() => onOpen(row)}>
                  {selectable && (
                    <td onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        size="small"
                        checked={selected.has(row.shipment_uuid)}
                        onChange={() => onToggle(row.shipment_uuid)}
                        slotProps={{ input: { "aria-label": `เลือก ${row.po_number}` } }}
                      />
                    </td>
                  )}
                  <td>
                    <BranchChip branches={branches} companyId={row.company_id} branchCode={row.branch_code} />
                  </td>
                  <td>
                    <span className="dc">{row.dc_code}</span> <span className="sub">{row.dc_name}</span>
                  </td>
                  <td>
                    {row.kind === "X" ? <Tag tone="warn">{roundLabel(row.kind, row.round)}</Tag> : roundLabel(row.kind, row.round)}
                  </td>
                  <td className="mono">{row.po_number}</td>
                  <td>{thaiDate(row.order_date)}</td>
                  <td>
                    {thaiDate(row.delivery_date)} {row.is_d_plus_1 && <Tag tone="info">D+1</Tag>}
                  </td>
                  <td className="n">{row.kind === "X" ? "—" : pcs(row.store_count)}</td>
                  <td className="n">{row.kind === "X" ? "—" : pcs(row.line_count)}</td>
                  <td className="n">{pcs(row.sku_count)}</td>
                  <td className="n">
                    <b>{pcs(delivered(row))}</b>
                    {delivered(row) !== row.pieces && <div className="sub">สั่ง {pcs(row.pieces)}</div>}
                  </td>
                  <td className="n">{pcs(row.full_cases)}</td>
                  <td className="n">{baht(row.subtotal)}</td>
                  <td className="n">{baht(row.vat)}</td>
                  <td className="n">{baht(row.total)}</td>
                  <td>
                    <StatusChip status={row.status} />
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={selectable ? 7 : 6}>รวม {rows.length} shipment</td>
                <td className="n">{pcs(sum(rows, (r) => r.store_count))}</td>
                <td className="n" />
                <td className="n" />
                <td className="n">{pcs(sum(rows, delivered))}</td>
                <td className="n">{pcs(sum(rows, (r) => r.full_cases))}</td>
                <td className="n">{baht(sum(rows, (r) => r.subtotal))}</td>
                <td className="n">{baht(sum(rows, (r) => r.vat))}</td>
                <td className="n">{baht(sum(rows, (r) => r.total))}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </Box>
      )}
    </Card>
  );
}

/**
 * BDC · คิวงาน — the shipments (one per PO = DC × รอบ) of a day, split into those delivered on
 * the order day and the D+1 (ต่างจังหวัด) ones, with their 1.5 status. Ticking rows opens the
 * selection bar: ยืนยัน DO / ออก INV in bulk (one PO at a time), พิมพ์เอกสาร (ศูนย์เอกสาร) and
 * สร้างไฟล์ 7-11. Head office gets พิมพ์เอกสาร alone (`bdcDesk` keeps the rest to the BDC team).
 * Filters live in the URL so the import screen can link straight to a day.
 */
export default function BdcQueue() {
  const navigate = useNavigate();
  const role = useUserRole();
  const canAct = featureAccess.bdcDesk(role);
  const [params, setParams] = useSearchParams();
  const { dcs } = useBdcDcs();
  const { busy, run } = useBlobExport();
  const { branches, ready: branchesReady } = useBdcBranches();
  const branch = useBdcBranchSelection(branches);

  const mode: DateMode = params.get("delivery_date") ? "delivery" : "order";
  const date = params.get(mode === "order" ? "order_date" : "delivery_date") || todayBkk();
  const dcCode = params.get("dc_code") || ALL;
  const round = params.get("round") || ALL;
  const statusParam = params.get("status");
  const status: BdcShipmentStatus | null = isBdcShipmentStatus(statusParam) ? statusParam : null;

  // the server filter excludes status so the count chips always show the whole day
  const filter = useMemo<BdcShipmentFilter>(
    () => ({
      ...(mode === "order" ? { order_date: date } : { delivery_date: date }),
      ...(dcCode !== ALL ? { dc_code: dcCode } : {}),
      ...(round !== ALL ? { round: Number(round) } : {}),
      ...(branch.companyId ? { company_id: branch.companyId } : {}),
    }),
    [mode, date, dcCode, round, branch.companyId],
  );

  const [allRows, setAllRows] = useState<BdcShipmentRow[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulk, setBulk] = useState<BulkAction | null>(null);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkResults, setBulkResults] = useState<BulkResult[] | null>(null);

  const load = useCallback(async () => {
    // wait for the branch list: a remembered branch is sent only once it is known to be visible
    if (!branchesReady) return;
    setLoading(true);
    try {
      const {
        data: { data },
      } = await listBdcShipments({ ...filter, page: 1, limit: BDC_QUEUE_LIMIT });
      const rows = data?.rows ?? [];
      setAllRows(rows);
      setCount(data?.count ?? 0);
      setSelected((current) => new Set(rows.map((r) => r.shipment_uuid).filter((uuid) => current.has(uuid))));
    } catch {
      setAllRows([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, [filter, branchesReady]);

  useEffect(() => {
    load();
  }, [load]);

  const update = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([key, value]) => {
      if (value === null || value === ALL) next.delete(key);
      else next.set(key, value);
    });
    setParams(next, { replace: true });
  };

  const handleMode = (event: ChangeEvent<HTMLInputElement>) => {
    const nextMode = event.target.value as DateMode;
    update(nextMode === "order" ? { order_date: date, delivery_date: null } : { delivery_date: date, order_date: null });
  };

  const handleDate = (value: Dayjs | null) => {
    if (!value || !value.isValid()) return;
    update({ [mode === "order" ? "order_date" : "delivery_date"]: value.format("YYYY-MM-DD") });
  };

  const counts = countByStatus(allRows);
  const rows = status ? allRows.filter((row) => row.status === status) : allRows;
  const sameDay = rows.filter((row) => !row.is_d_plus_1);
  const nextDay = rows.filter((row) => row.is_d_plus_1);
  const withStores = rows.filter((row) => row.kind === "A");
  const ticked = allRows.filter((row) => selected.has(row.shipment_uuid));

  const open = (row: BdcShipmentRow) => navigate(bdcShipmentLink(row.po_number));
  const pickedBranch = branches.find((b) => b.company_id === branch.companyId);
  // with one branch picked, the DC filter offers that branch's DCs
  const dcOptions = branch.companyId ? dcs.filter((dc) => dc.company_id === branch.companyId) : dcs;

  const handleBranch = (value: string) => {
    branch.select(value);
    const dc = dcs.find((d) => d.dc_code === dcCode);
    if (dc && value !== ALL_BRANCHES && dc.company_id !== value) update({ dc_code: null });
  };
  const branchSuffix = pickedBranch?.branch_code ? `-${pickedBranch.branch_code}` : "";

  const toggle = (uuid: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(uuid)) next.delete(uuid);
      else next.add(uuid);
      return next;
    });
  const toggleAll = (list: BdcShipmentRow[], on: boolean) =>
    setSelected((current) => {
      const next = new Set(current);
      list.forEach((r) => (on ? next.add(r.shipment_uuid) : next.delete(r.shipment_uuid)));
      return next;
    });

  const plan = bulk ? bulkTargets(allRows, selected, bulk) : null;

  const runBulkAction = async () => {
    if (!bulk || !plan) return;
    setBulkRunning(true);
    const results = await runBulk(
      plan.targets,
      async (po) => {
        if (bulk === "confirm_do") {
          await confirmBdcDo(po);
          return "ยืนยัน DO แล้ว";
        }
        const {
          data: { data },
        } = await issueBdcInvoice(po);
        return data?.invoice_no ? `ออก INV ${data.invoice_no}` : "ออก INV แล้ว";
      },
      (error) => {
        const failure = readBdcError(error, `${BULK_LABEL[bulk]} ไม่สำเร็จ`);
        return { message: failure.message, stopAll: issueFailureKind(failure) === "sequence" };
      },
    );
    setBulkRunning(false);
    setBulk(null);
    setBulkResults(results);
    load();
  };

  const ediUuids = ticked
    .filter((r) => r.status === "do_confirmed" || r.status === "invoiced")
    .map((r) => r.shipment_uuid);

  return (
    <Box sx={{ padding: "8px 0" }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={2} flexWrap="wrap" mb={2}>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            BDC · คิวงาน
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Shipment ต่อ DC × รอบ เรียงตามวันส่ง → DC → รอบ · คลิกแถวเพื่อปรับยอดส่ง ยืนยัน DO และออก INV
          </Typography>
        </Box>
        <Stack direction="row" gap={1} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            loading={busy === "queue"}
            disabled={allRows.length === 0}
            onClick={() => run("queue", () => exportBdcShipments(filter), `bdc-queue-${date}${branchSuffix}.xlsx`)}
          >
            Export Excel
          </Button>
          <Tooltip title={mode === "order" ? "" : "ใบคุมทั้งวันออกตามวันที่สั่ง — เปลี่ยนตัวกรองเป็นวันที่สั่งก่อน"}>
            <span>
              <Button
                variant="contained"
                startIcon={<FileDownloadIcon />}
                loading={busy === "control"}
                disabled={mode !== "order" || withStores.length === 0}
                onClick={() =>
                  run("control", () => exportBdcControlSheet(date, branch.companyId), `bdc-control-sheet-${date}${branchSuffix}.xlsx`)
                }
              >
                Export ใบคุมทั้งวัน (Excel)
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      <Stack gap={2} sx={TABLE_SX}>
        <Card sx={{ ...CARD_SX, p: 2 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
            <Stack direction="row" gap={1.5} flexWrap="wrap" alignItems="center">
              <BranchSelector branches={branches} value={branch.selected} onChange={handleBranch} />
              <TextField select size="small" label="กรองตาม" value={mode} onChange={handleMode} sx={{ width: 140 }}>
                <MenuItem value="order">วันที่สั่ง</MenuItem>
                <MenuItem value="delivery">วันส่ง</MenuItem>
              </TextField>
              <DatePicker
                label={mode === "order" ? "วันที่สั่ง" : "วันส่ง"}
                value={dayjs(date)}
                format="DD/MM/YYYY"
                onChange={handleDate}
                slotProps={{ textField: { size: "small", sx: { width: 180 } } }}
              />
              <Typography variant="body2" color="text.secondary">
                {thaiDate(date)}
              </Typography>
              <TextField select size="small" label="DC" value={dcCode} onChange={(e) => update({ dc_code: e.target.value })} sx={{ minWidth: 200 }}>
                <MenuItem value={ALL}>ทุก DC</MenuItem>
                {dcOptions.map((dc) => (
                  <MenuItem key={dc.dc_code} value={dc.dc_code}>
                    {dc.dc_code} {dc.dc_name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField select size="small" label="รอบ" value={round} onChange={(e) => update({ round: e.target.value })} sx={{ width: 120 }}>
                <MenuItem value={ALL}>ทุกรอบ</MenuItem>
                {ROUND_OPTIONS.map((r) => (
                  <MenuItem key={r} value={String(r)}>
                    รอบ {r}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </LocalizationProvider>
        </Card>

        <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center" data-testid="bdc-status-counts">
          <Chip
            size="small"
            label={`ทั้งหมด ${allRows.length}`}
            color={status ? "default" : "primary"}
            variant={status ? "outlined" : "filled"}
            onClick={() => update({ status: null })}
          />
          {BDC_STATUS_ORDER.map((key) => {
            const tone = TAG_COLORS[BDC_STATUS_META[key].tone];
            const active = status === key;
            return (
              <Chip
                key={key}
                size="small"
                label={`${BDC_STATUS_META[key].label} ${counts[key]}`}
                onClick={() => update({ status: active ? null : key })}
                sx={{
                  bgcolor: tone.bg,
                  color: tone.fg,
                  border: active ? `1.5px solid ${tone.fg}` : "1.5px solid transparent",
                  fontWeight: active ? 600 : 400,
                }}
              />
            );
          })}
        </Stack>

        <TestModeBanner branches={branch.viewed} />

        {ticked.length > 0 && (
          <Box
            data-testid="bdc-selection-bar"
            sx={{
              bgcolor: "#363A44",
              color: "#FFFFFF",
              borderRadius: "10px",
              px: 1.75,
              py: 1.25,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 1.25,
            }}
          >
            <Typography variant="body2">เลือก {ticked.length} shipment</Typography>
            <Button size="small" sx={{ color: "#FFFFFF" }} onClick={() => setSelected(new Set())}>
              ล้าง
            </Button>
            <Box sx={{ flex: 1 }} />
            {canAct && (["confirm_do", "issue"] as BulkAction[]).map((action) => (
              <Button
                key={action}
                size="small"
                variant="contained"
                sx={{ bgcolor: "#FFFFFF", color: "#22252C", "&:hover": { bgcolor: "#F1F2F5" } }}
                disabled={bulkTargets(allRows, selected, action).targets.length === 0}
                onClick={() => setBulk(action)}
              >
                {BULK_LABEL[action]}
              </Button>
            ))}
            <Button
              size="small"
              variant="contained"
              sx={{ bgcolor: "#FFFFFF", color: "#22252C", "&:hover": { bgcolor: "#F1F2F5" } }}
              onClick={() =>
                navigate(
                  bdcDocumentsLink({
                    ...(mode === "order" ? { orderDate: date } : { deliveryDate: date }),
                    select: ticked.map((r) => r.shipment_uuid),
                  }),
                )
              }
            >
              พิมพ์เอกสาร
            </Button>
            {canAct && (
              <Button
                size="small"
                variant="contained"
                sx={{ bgcolor: "#FDA42C", color: "#22252C", "&:hover": { bgcolor: "#F39A1F" } }}
                disabled={ediUuids.length === 0}
                onClick={() =>
                  navigate(
                    bdcEdiLink({ ...(mode === "order" ? { orderDate: date } : { deliveryDate: date }), select: ediUuids }),
                  )
                }
              >
                สร้างไฟล์ 7-11
              </Button>
            )}
          </Box>
        )}

        {loading && <LinearProgress />}

        <KpiRow
          items={[
            {
              label: "Shipment (DC × รอบ)",
              value: pcs(withStores.length),
              sub: `${new Set(withStores.map((r) => r.dc_code)).size} DC${rows.length > withStores.length ? ` · + ไม่มีรายร้าน ${rows.length - withStores.length}` : ""}`,
            },
            { label: "ส่งวันนี้", value: pcs(sameDay.length), sub: `${pcs(sum(sameDay, delivered))} ชิ้น` },
            { label: "ส่ง D+1", value: pcs(nextDay.length), sub: `${pcs(sum(nextDay, delivered))} ชิ้น` },
            {
              label: "ชิ้นส่งทั้งหมด",
              value: pcs(sum(rows, delivered)),
              sub: `สั่ง ${pcs(sum(rows, (r) => r.pieces))} · ${pcs(sum(withStores, (r) => r.store_count))} ร้าน`,
            },
            { label: "มูลค่ารวม (รวม VAT)", value: `฿${baht(sum(rows, (r) => r.total))}`, sub: "ตาม PO ของ 7-11" },
          ]}
        />

        {count > allRows.length && (
          <Alert severity="info">
            แสดง {pcs(allRows.length)} จาก {pcs(count)} shipment — กรองตาม DC หรือรอบเพื่อดูส่วนที่เหลือ
          </Alert>
        )}

        {[
          { title: "ส่งวันนี้ (วันเดียวกับวันที่สั่ง)", list: sameDay },
          { title: "ส่ง D+1 (ต่างจังหวัด)", list: nextDay },
        ].map(({ title, list }) => (
          <ShipmentTable
            key={title}
            title={title}
            rows={list}
            branches={branches}
            selected={selected}
            selectable
            onToggle={toggle}
            onToggleAll={toggleAll}
            onOpen={open}
          />
        ))}
      </Stack>

      <Dialog open={bulk !== null} onClose={bulkRunning ? undefined : () => setBulk(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {bulk ? BULK_LABEL[bulk] : ""} · {pcs(plan?.targets.length ?? 0)} PO
        </DialogTitle>
        <DialogContent sx={TABLE_SX}>
          {bulk === "confirm_do" && (
            <Typography variant="body2" mb={1}>
              ยอดส่งของ PO เหล่านี้จะถูกล็อก แก้ไม่ได้จนกว่าจะยกเลิกยืนยัน
            </Typography>
          )}
          {bulk === "issue" && (
            <Typography variant="body2" mb={1}>
              ออก INV ทีละ PO ตามลำดับ · ถ้าเลข INV ถัดไปเคยใช้แล้วระบบหยุดทันที
            </Typography>
          )}
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {plan?.targets.map((row) => (
              <li key={row.shipment_uuid}>
                {row.dc_code} {roundLabel(row.kind, row.round)} · PO {row.po_number}
              </li>
            ))}
          </Box>
          {plan && plan.skipped.length > 0 && (
            <Alert severity="info" sx={{ mt: 1.5 }}>
              ข้าม {plan.skipped.length} PO ที่สถานะไม่ใช่ {bulk === "confirm_do" ? "ร่าง" : "ยืนยัน DO"}:{" "}
              {plan.skipped.map((r) => r.po_number).join(", ")}
            </Alert>
          )}
          {bulkRunning && <LinearProgress sx={{ mt: 1.5 }} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulk(null)} disabled={bulkRunning}>
            ยกเลิก
          </Button>
          <Button variant="contained" loading={bulkRunning} disabled={!plan?.targets.length} onClick={runBulkAction}>
            {bulk ? BULK_LABEL[bulk] : ""}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkResults !== null} onClose={() => setBulkResults(null)} maxWidth="sm" fullWidth>
        <DialogTitle>ผลการทำรายการ</DialogTitle>
        <DialogContent sx={TABLE_SX}>
          <table className="bdc-t" data-testid="bdc-bulk-results">
            <tbody>
              {bulkResults?.map((result) => (
                <tr key={result.po_number}>
                  <td className="mono">{result.po_number}</td>
                  <td>{result.ok ? <Tag tone="ok">สำเร็จ</Tag> : <Tag tone="crit">ไม่สำเร็จ</Tag>}</td>
                  <td>{result.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkResults(null)}>ปิด</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
